import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import FaceIcon from "@mui/icons-material/Face";
import { Talent } from "../../types/users";
import { useTitle } from "../../hooks/useTitle";
import TalentDrawer from "./TalentDrawer";
import {
  Circle,
  GoogleMap,
  useLoadScript,
  OverlayView,
  Marker,
} from "@react-google-maps/api";
import { GMAP_API_KEY } from "../../configs/api";
import { useCompany } from "../../hooks/useCompany";
import {
  useGetTalentsAllQuery,
  useGetTalentsQuery,
} from "../../services/talent.service";
import { getDistanceBetweenPoints } from "../../utils/mapHelper";
import { useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import { FilterAlt, FilterAltOutlined } from "@mui/icons-material";
import ModalTalentFilter from "./ModalTalentFilter";
import { GridSortModel } from "@mui/x-data-grid";
import { CardBase } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const options = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
};

const CustomMarker = (props: any) => {
  const { name, photo, onClick } = props;

  return (
    <OverlayView {...props}>
      <Box sx={{ marginTop: -5.5, marginLeft: -2.5 }}>
        <Avatar alt={name} src={photo?.[0]?.original_url} onClick={onClick} />
      </Box>
    </OverlayView>
  );
};

const DEFAULT_ZOOM = 4.3; // zoom out to show all US
const DEFAULT_RADIUS = 11000; //m --> changed according to DEFAULT_ZOOM
const mapLibraries = ["places", "geometry"];

const TalentsMapPage = () => {
  const independentProfessionalsMapLabel = useUILabel("talent.independentProfessionalsMap");
  const searchingIndependentProfessionalsLabel = useUILabel("talent.searchingIndependentProfessionals");
  const filterLabel = useUILabel("common.filter");
  const refreshLabel = useUILabel("common.refresh");
  const loadingMapsLabel = useUILabel("talent.loadingMaps");
  
  useTitle(independentProfessionalsMapLabel);
  const company = useCompany();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [showFilter, setShowFilter] = React.useState(false);
  // const [center, setCenter] = React.useState({
  //   lat: company?.coordinate?.coordinates[1] || 0,
  //   lng: company?.coordinate?.coordinates[0] || 0,
  // });
  const [center, setCenter] = React.useState({
    // central coordinate for United States
    lat: 37.0902,
    lng: -95.7129,
  });
  const [searchParam, setSearchParam] = React.useState({
    lat: company?.coordinate?.coordinates[1] || 0,
    lng: company?.coordinate?.coordinates[0] || 0,
    radius: DEFAULT_RADIUS,
  });

  // const { data, isFetching } = useGetTalentsAllQuery({
  //   distance: `${searchParam.lat}|${searchParam.lng}|${searchParam.radius}`,
  // });

  const searchFilter = useAppSelector(
    (state: RootState) => state.ui.talentFilter
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const { data, isLoading, isFetching } = useGetTalentsQuery({
    page: 0,
    pageSize: 999,
    distance: `${searchParam.lat}|${searchParam.lng}|${searchParam.radius}`,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    load: "country,photo,skills,languages",
    search: searchQuery,
    ...searchFilter,
  });

  // ?sort=first_name%7Casc&load=photo%2Clanguages%2Cskills&distance=29.7246822%7C-95.5564777%7C11000
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Talent | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GMAP_API_KEY,
    //@ts-ignore
    libraries: mapLibraries,
  });

  const filterOn = React.useMemo(() => {
    return (
      (Array.isArray(searchFilter.skills) && searchFilter.skills.length > 0) ||
      !!searchFilter.state ||
      !!searchFilter.city ||
      !!searchFilter.business_verticals ||
      !!searchFilter.work_status
    );
  }, [searchFilter]);

  const mapRef = React.useRef<google.maps.Map>();

  const onMapLoad = React.useCallback(
    // @ts-ignore
    (map) => {
      map.setCenter(center);
      mapRef.current = map;
    },
    [center]
  );

  const getRadius = React.useCallback(() => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      if (!bounds) return null;

      // computeDistanceBetween returns meters
      const nsRadius = google.maps.geometry.spherical.computeDistanceBetween(
        bounds.getCenter().toJSON(),
        {
          lat: bounds.getNorthEast().toJSON().lat,
          lng: bounds.getCenter().toJSON().lng,
        }
      );
      // console.log(`nsRadius: ${nsRadius.toFixed(2)}`);
      const ewRadius = google.maps.geometry.spherical.computeDistanceBetween(
        bounds.getCenter().toJSON(),
        {
          lat: bounds.getCenter().toJSON().lat,
          lng: bounds.getNorthEast().toJSON().lng,
        }
      );
      const radius = nsRadius >= ewRadius ? nsRadius : ewRadius;
      return Number(radius.toFixed(0));
    }
    return null;
  }, []);

  const onDragEnd = React.useCallback(() => {
    if (!mapRef.current) return;
    const lat = mapRef.current?.getCenter()?.lat() || 0;
    const lng = mapRef.current?.getCenter()?.lng() || 0;
    const dist = getDistanceBetweenPoints(
      { lat, lng },
      { lat: center.lat, lng: center.lng }
    );
    if (dist >= 1) {
      // if center changed more than 1 mile
      setCenter({ lat, lng });
      setSearchParam({ lat, lng, radius: getRadius() || DEFAULT_RADIUS });
    }
  }, [getRadius]);

  const onZoomChanged = React.useCallback(() => {
    if (!mapRef.current) return;
    const lat = mapRef.current?.getCenter()?.lat() || 0;
    const lng = mapRef.current?.getCenter()?.lng() || 0;
    setCenter({ lat, lng });
    setSearchParam({ lat, lng, radius: getRadius() || DEFAULT_RADIUS });
  }, [getRadius]);

  const handleMarkerClick = (item: Talent) => {
    setCurrentItem(item);
    setOpen(true);
  };

  if (loadError) {
    return (
      <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
        <CardBase
          title="Error"
          useGlassCard={true}
          nested={false}
        >
          <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
            <Typography variant="h6" color="error" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
              Error loading maps
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              Please refresh the page or contact support if the problem persists.
            </Typography>
          </Box>
        </CardBase>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
        <CardBase
          title="Loading Maps"
          useGlassCard={true}
          nested={false}
        >
          <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              Loading Google Maps...
            </Typography>
          </Box>
        </CardBase>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
      <CardBase
        title={independentProfessionalsMapLabel}
        useGlassCard={true}
        nested={false}
        sx={{
          "& .card-base__header": {
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 1.5, sm: 2 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
          },
          "& .card-base__header > .MuiBox-root:first-of-type": { flex: 1 },
        }}
        headerActions={
          <Button
            variant={filterOn ? "contained" : "outlined"}
            color="success"
            onClick={() => setShowFilter(true)}
            startIcon={filterOn ? <FilterAlt /> : <FilterAltOutlined />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minWidth: { xs: "100%", sm: 140 },
              width: { xs: "100%", sm: "auto" },
              color: filterOn ? "#fff" : "#12903C",
              borderColor: "#12903C",
              backgroundColor: filterOn ? "#12903C" : "transparent",
              boxShadow: filterOn
                ? isDark
                  ? "0 4px 12px rgba(18, 144, 60, 0.4)"
                  : "0 4px 12px rgba(18, 144, 60, 0.3)"
                : "none",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-2px)",
                backgroundColor: filterOn ? "#12903C" : alpha("#12903C", 0.1),
                borderColor: "#12903C",
                color: filterOn ? "#fff" : "#12903C",
                boxShadow: filterOn
                  ? isDark
                    ? "0 6px 16px rgba(18, 144, 60, 0.5)"
                    : "0 6px 16px rgba(18, 144, 60, 0.4)"
                  : isDark
                    ? "0 4px 12px rgba(18, 144, 60, 0.2)"
                    : "0 4px 12px rgba(18, 144, 60, 0.15)",
              },
            }}
          >
            {filterLabel} {filterOn ? "ON" : ""}
          </Button>
        }
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: "70vh", sm: "85vh" },
            borderRadius: { xs: "8px", sm: "12px" },
            overflow: "hidden",
          }}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            // center={center}
            zoom={DEFAULT_ZOOM}
            options={options}
            onDragEnd={onDragEnd}
            onZoomChanged={onZoomChanged}
            onLoad={onMapLoad}
          >
            <React.Fragment>
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: 8, sm: 16 },
                  left: { xs: 8, sm: 16 },
                  right: { xs: 8, sm: "auto" },
                  zIndex: 1,
                }}
              >
                {isFetching ? (
                  <Chip
                    icon={<CircularProgress size={20} />}
                    label={searchingIndependentProfessionalsLabel}
                    sx={{
                      background: isDark
                        ? alpha("#ffffff", 0.15)
                        : alpha("#ffffff", 0.9),
                      backdropFilter: "blur(10px) saturate(180%)",
                      WebkitBackdropFilter: "blur(10px) saturate(180%)",
                      border: `1px solid ${isDark ? alpha("#ffffff", 0.2) : alpha("#000000", 0.1)}`,
                      boxShadow: isDark
                        ? "0 4px 16px rgba(0, 0, 0, 0.3)"
                        : "0 4px 16px rgba(0, 0, 0, 0.1)",
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      height: { xs: 28, sm: 32 },
                    }}
                  />
                ) : (
                  <>
                    {!data?.data.length ? (
                      <Chip
                        label="IPs not found, try moving to another location or changing zoom level. Please reach out to Workz360 support at info@workz360.com"
                        color="warning"
                        sx={{
                          background: isDark
                            ? alpha("#ff9800", 0.2)
                            : alpha("#ff9800", 0.15),
                          backdropFilter: "blur(10px) saturate(180%)",
                          WebkitBackdropFilter: "blur(10px) saturate(180%)",
                          border: `1px solid ${isDark ? alpha("#ff9800", 0.4) : alpha("#ff9800", 0.3)}`,
                          boxShadow: isDark
                            ? "0 4px 16px rgba(0, 0, 0, 0.3)"
                            : "0 4px 16px rgba(0, 0, 0, 0.1)",
                          fontWeight: 600,
                          maxWidth: { xs: "calc(100vw - 32px)", sm: 400 },
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          height: { xs: "auto", sm: 32 },
                          py: { xs: 0.5, sm: 0 },
                          "& .MuiChip-label": {
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                            wordBreak: "break-word",
                            px: { xs: 1, sm: 1.5 },
                          },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<FaceIcon />}
                        label={`${data?.data.length} IPs found`}
                        color="success"
                        sx={{
                          background: isDark
                            ? alpha("#12903C", 0.2)
                            : alpha("#12903C", 0.15),
                          backdropFilter: "blur(10px) saturate(180%)",
                          WebkitBackdropFilter: "blur(10px) saturate(180%)",
                          border: `1px solid ${isDark ? alpha("#12903C", 0.4) : alpha("#12903C", 0.3)}`,
                          boxShadow: isDark
                            ? "0 4px 16px rgba(0, 0, 0, 0.3)"
                            : "0 4px 16px rgba(18, 144, 60, 0.2)",
                          fontWeight: 600,
                          color: "#12903C",
                          fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          height: { xs: 28, sm: 32 },
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
              {/* @ts-ignore */}
              <OverlayView
                key={"#company-marker"}
                position={{
                  lat: company?.coordinate?.coordinates[1] || 0,
                  lng: company?.coordinate?.coordinates[0] || 0,
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <Box sx={{ marginTop: -2.5, marginLeft: -2.5 }}>
                  <Chip
                    icon={<HomeRepairServiceIcon />}
                    label={company?.name || "Company"}
                    color="success"
                    sx={{
                      background: isDark
                        ? alpha("#12903C", 0.2)
                        : alpha("#12903C", 0.15),
                      backdropFilter: "blur(10px) saturate(180%)",
                      WebkitBackdropFilter: "blur(10px) saturate(180%)",
                      border: `1px solid ${isDark ? alpha("#12903C", 0.4) : alpha("#12903C", 0.3)}`,
                      boxShadow: isDark
                        ? "0 4px 16px rgba(0, 0, 0, 0.3)"
                        : "0 4px 16px rgba(18, 144, 60, 0.2)",
                      fontWeight: 600,
                      color: "#12903C",
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      height: { xs: 28, sm: 32 },
                      "& .MuiChip-label": {
                        maxWidth: { xs: 120, sm: "none" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Box>
              </OverlayView>

              {/*
             * for debuging change circle component propreties visible=true
             */}
              {/* <Circle
            center={center} // required
            radius={searchParam.radius} // required
            options={{
              strokeColor: colors.red[400],
              fillColor: colors.red[400],
            }}
            visible={true}
          /> */}

              {/* {data?.data.map((talent) => (
            <CustomMarker
              id={talent.id}
              key={`#talent-marker-${talent.id}`}
              name={talent.full_name}
              photo={talent.photo}
              position={{
                lat: talent.coordinate?.coordinates[1] || 0,
                lng: talent.coordinate?.coordinates[0] || 0,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              onClick={() => handleMarkerClick(talent)}
            />
          ))} */}
              {data?.data.map((talent) => {
                return (
                  <Marker
                    icon={{
                      url: "/talent-marker.png",
                      scaledSize: new window.google.maps.Size(20, 35),
                    }}
                    key={`#talent-marker-${talent.id}`}
                    position={{
                      lat: talent.coordinate?.coordinates[1] || 0,
                      lng: talent.coordinate?.coordinates[0] || 0,
                    }}
                    // mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    onClick={() => handleMarkerClick(talent)}
                  />
                );
              })}
            </React.Fragment>
          </GoogleMap>
          <TalentDrawer data={currentItem!} open={open} setOpen={setOpen} />
          <ModalTalentFilter
            open={showFilter}
            onClose={() => setShowFilter(false)}
          />
        </Box>
      </CardBase>
    </Box>
  );
};
export default TalentsMapPage;
